import React from "react";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { Button } from "@mui/material";
import FullLogo from "../Assist/FullLogo.png";

const PRTOPDF = ({ results, sortedRequests }) => {
  const drawTextOnPage = (page, request, fonts) => {
    const { font, boldFont } = fonts;
    const { width, height } = page.getSize();
    const primaryColor = rgb(26 / 255, 35 / 255, 126 / 255); // #1A237E
    const secondaryColor = rgb(69 / 255, 90 / 255, 100 / 255); // #455A64
    const textColor = rgb(33 / 255, 33 / 255, 33 / 255); // #212121

    // Top Accent Bar
    page.drawRectangle({ x: 0, y: height - 10, width, height: 10, color: primaryColor });

    // Title
    page.drawText("PURCHASE REQUISITION", {
      x: width - 280,
      y: height - 60,
      size: 20,
      font: boldFont,
      color: primaryColor,
    });
    
    page.drawText(`PR NO: ${request.PR_no || "N/A"}`, {
      x: width - 150,
      y: height - 80,
      size: 10,
      font: font,
      color: secondaryColor,
    });

    // Divider
    page.drawLine({
      start: { x: 50, y: height - 110 },
      end: { x: width - 50, y: height - 110 },
      thickness: 1,
      color: rgb(207 / 255, 216 / 255, 220 / 255),
    });

    // Section Titles
    page.drawText("REQUESTER DETAILS", { x: 50, y: height - 140, size: 10, font: boldFont, color: primaryColor });
    page.drawText("PURCHASE DETAILS", { x: width / 2, y: height - 140, size: 10, font: boldFont, color: primaryColor });

    const drawRow = (label, value, x, y) => {
      page.drawText(label, { x, y, size: 9, font: boldFont, color: secondaryColor });
      page.drawText(String(value || "—"), { x: x + 80, y, size: 9, font: font, color: textColor });
    };

    // Left Column
    let leftY = height - 165;
    drawRow("Name:", request.body?.sender_name, 50, leftY);
    drawRow("Email:", request.body?.usermail, 50, leftY - 20);
    drawRow("Location:", request.body?.location, 50, leftY - 40);
    drawRow("Date:", request.body?.currentDate, 50, leftY - 60);

    // Right Column
    let rightY = height - 165;
    drawRow("Category:", request.body?.productCategory, width / 2, rightY);
    drawRow("Type:", request.body?.purchaseType, width / 2, rightY - 20);
    drawRow("Payment:", request.body?.paymentType, width / 2, rightY - 40);
    drawRow("Expected:", request.body?.expectedDate, width / 2, rightY - 60);

    // Order Items Section
    let sectionY = leftY - 100;
    page.drawText("ORDER ITEM DETAILS", { x: 50, y: sectionY, size: 10, font: boldFont, color: primaryColor });

    sectionY -= 25;
    const drawItemRow = (label, value, y) => {
      page.drawText(label, { x: 50, y, size: 9, font: boldFont, color: secondaryColor });
      page.drawText(String(value || "—"), { x: 150, y, size: 9, font: font, color: textColor });
    };

    drawItemRow("Product:", request.body?.productName, sectionY);
    drawItemRow("Description:", request.body?.productDescription, sectionY - 20);
    drawItemRow("Reason:", request.body?.productReason, sectionY - 40);
    drawItemRow("Quantity:", request.body?.quantity, sectionY - 60);
    drawItemRow("Cost:", request.body?.approxCost, sectionY - 80);
    drawItemRow("Comments:", request.body?.comment, sectionY - 100);
    drawItemRow("Status:", request.status, sectionY - 120);

    // Approvals Table
    let tableY = sectionY - 160;
    page.drawText("APPROVAL HISTORY", { x: 50, y: tableY, size: 10, font: boldFont, color: primaryColor });

    tableY -= 20;
    // Table Header Background
    page.drawRectangle({ x: 50, y: tableY - 5, width: width - 100, height: 20, color: primaryColor });
    
    const colX = { mail: 60, comment: 220, response: 450 };
    page.drawText("Approver Email", { x: colX.mail, y: tableY, size: 9, font: boldFont, color: rgb(1, 1, 1) });
    page.drawText("Comment", { x: colX.comment, y: tableY, size: 9, font: boldFont, color: rgb(1, 1, 1) });
    page.drawText("Status", { x: colX.response, y: tableY, size: 9, font: boldFont, color: rgb(1, 1, 1) });

    let rowY = tableY - 20;
    const tableData = request.reportingToAndSupervisor || [];
    
    tableData.forEach((entry, i) => {
      // Alternate row background
      if (i % 2 === 0) {
        page.drawRectangle({ x: 50, y: rowY - 5, width: width - 100, height: 20, color: rgb(245/255, 247/255, 250/255) });
      }
      
      page.drawText(entry.sendTo || "—", { x: colX.mail, y: rowY, size: 8, font: font, color: textColor });
      
      // Truncate comment if too long
      const cmt = entry.Comment || "—";
      const displayCmt = cmt.length > 40 ? cmt.substring(0, 40) + "..." : cmt;
      page.drawText(displayCmt, { x: colX.comment, y: rowY, size: 8, font: font, color: textColor });
      
      page.drawText(entry.response || "—", { x: colX.response, y: rowY, size: 8, font: boldFont, color: primaryColor });
      
      rowY -= 20;
    });

    // Footer
    page.drawText("Generated by ITRadiant Purchase System", {
      x: 50,
      y: 30,
      size: 8,
      font: font,
      color: rgb(158/255, 158/255, 158/255),
    });
  };

  const handleDownloadPDF = async () => {
    const pdfDoc = await PDFDocument.create();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
    const fonts = { font, boldFont };

    const selectedAndFilteredPDFs = results.length > 0 ? results : sortedRequests;

    // Load logo
    const logoBytes = await fetch(FullLogo).then((res) => res.arrayBuffer());
    const logoImage = await pdfDoc.embedPng(logoBytes);
    const logoDims = logoImage.scale(0.18); 

    selectedAndFilteredPDFs.forEach((request) => {
      const page = pdfDoc.addPage([595.28, 841.89]); // A4 Size

      // Draw logo
      page.drawImage(logoImage, {
        x: 50,
        y: page.getHeight() - logoDims.height - 35,
        width: logoDims.width,
        height: logoDims.height,
      });

      drawTextOnPage(page, request, fonts);
    });

    const pdfBytes = await pdfDoc.save();
    const blob = new Blob([pdfBytes], { type: "application/pdf" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "PR_Admin_Reports.pdf";
    link.click();
  };

  return (
    <Button
      onClick={handleDownloadPDF}
      variant="contained"
      color="primary"
      size="small"
      sx={{
        fontSize: "13px",
        padding: "3px 8px",
        minWidth: "auto",
        textTransform: "none",
      }}
    >
      Download PDF
    </Button>
  );
};

export default PRTOPDF;
